import React, { FC, useState, useRef, useEffect } from 'react';
import jsYaml from 'js-yaml';
import { formatToolDisplay, ToolGroup } from '../../../utils/toolFormatting';
import SidewaysLoading from './SidewaysLoading';
import { ReactComponent as ExpandIcon } from '../../../icons/expand.svg';
import { ReactComponent as CollapseIcon } from '../../../icons/collapse.svg';
import { ReactComponent as CodeSquareIcon } from '../../../icons/code-square.svg';
import { ReactComponent as DescriptionSquareIcon } from '../../../icons/description-square.svg';
import { AgentMessage } from '@dreamai/types';
import './chat-tool-group-display.scss';

// Default color scheme for tool messages when no predominant color is provided
const DEFAULT_TOOL_COLORS = {
  background: '#000',
  borderColor: '#cbd5e0',
  alternate: '#fff',
};

interface ChatToolGroupDisplayProps {
  toolGroup: ToolGroup;
  isActive?: boolean;
  colors?: { background: string; borderColor: string; alternate: string };
}

/**
 * Component to display grouped tool messages in the chat
 * This handles rendering for multiple related tool calls
 */
const ChatToolGroupDisplay: FC<ChatToolGroupDisplayProps> = ({
  toolGroup,
  isActive = false,
  colors,
}) => {
  // Get the family name and count of tools
  const { tools } = toolGroup;

  // State to track the most recent tool and animation states
  const [currentToolIndex, setCurrentToolIndex] = useState(-1); // Start collapsed
  const [showAudit, setShowAudit] = useState(false);
  const [showAllTools, setShowAllTools] = useState(false);
  const prevToolsLengthRef = useRef(tools.length);

  // No need to initialize display text with the new accordion approach

  // Update when tools change
  useEffect(() => {
    // Keep track of tools length but don't auto-expand anymore
    prevToolsLengthRef.current = tools.length;
  }, [tools.length]);

  // Toggle between human description and audit view
  const toggleAudit = (e: React.MouseEvent) => {
    e.stopPropagation();
    setShowAudit(!showAudit);
  };

  // Get the tool name to display in the header
  const getToolName = (tool: any) => {
    if (!tool || !tool.content) return 'No Content';

    if (typeof tool.content === 'string') {
      return formatToolDisplay(tool.content, false);
    }

    return 'Unsupported Tool View';
  };

  // Format the input parameters as YAML for the audit view
  const getAuditYaml = (tool: AgentMessage) => {
    if (!tool || !tool.tool_call_input) return '';

    // Create a copy of the input without human_understandable_description
    const inputForAudit = { ...tool.tool_call_input };
    delete inputForAudit.human_understandable_description;

    try {
      return jsYaml.dump(inputForAudit);
    } catch (e) {
      return 'Error formatting tool input';
    }
  };

  // Get human understandable description or fallback text
  const getHumanDescription = (tool: AgentMessage) => {
    if (tool?.tool_call_input?.human_understandable_description) {
      return tool.tool_call_input.human_understandable_description;
    }
    return <em>No further detail</em>;
  };

  // Use provided colors or default colors
  const toolColors = colors || DEFAULT_TOOL_COLORS;
  const background: string = toolColors.background;
  const borderColor: string = toolColors.borderColor;

  // Determine which tools to show
  const toolsToShow = showAllTools ? tools : [tools[0]];
  const remainingToolsCount = tools.length - 1;

  return (
    <div className='tool-message-content tool-group'>
      {/* Show all tools in an accordion style list */}
      <div className='tool-accordion'>
        {toolsToShow.map((tool: any, index: number) => {
          // For the original index when showing all tools
          const originalIndex = showAllTools ? index : 0;
          const isToolActive = isActive && originalIndex === tools.length - 1;
          const isToolSelected = currentToolIndex === originalIndex;

          return (
            <div key={originalIndex} className='tool-accordion-item'>
              {/* Tool header - always visible */}
              <div
                className={`tool-header ${isToolSelected ? 'selected' : ''}`}
                style={{
                  borderColor,
                  background: isToolSelected
                    ? background
                    : toolColors.alternate,
                  borderWidth: isToolSelected ? '0px' : '1px',
                }}
                onClick={(e) => {
                  e.stopPropagation();
                  setCurrentToolIndex(isToolSelected ? -1 : originalIndex); // Toggle selection
                }}
              >
                <div className='header-left'>
                  <div className='tool-title'>
                    {getToolName(tool)}
                    {/* Show "+ X more" for the first tool when collapsed */}
                    {!showAllTools &&
                      originalIndex === 0 &&
                      remainingToolsCount > 0 && (
                        <span
                          className='more-actions-link'
                          onClick={(e) => {
                            e.stopPropagation();
                            setShowAllTools(true);
                          }}
                        >
                          {' '}
                          + {remainingToolsCount} more
                        </span>
                      )}
                    {/* Show "Hide actions" for the last tool when expanded */}
                    {showAllTools && originalIndex === tools.length - 1 && (
                      <span
                        className='more-actions-link'
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowAllTools(false);
                        }}
                      >
                        {' '}
                        Hide actions
                      </span>
                    )}
                  </div>

                  {/* Loading indicator for active tools */}
                  {isToolActive && (
                    <span className='sideways-loading-icon'>
                      <SidewaysLoading />
                    </span>
                  )}
                </div>

                {/* Icon for expanded/collapsed state */}
                <span className='accordion-icon'>
                  {isToolSelected ? <CollapseIcon /> : <ExpandIcon />}
                </span>
              </div>

              {/* Tool details - only visible when selected */}
              {isToolSelected && (
                <div className='tool-details' style={{ background }}>
                  {/* Tool description div that contains both description and audit */}
                  <div className='tool-description'>
                    {/* Content area - shows either description or audit */}
                    {!showAudit ? (
                      /* Human description */
                      getHumanDescription(tool)
                    ) : (
                      /* Tool audit view */
                      <div className='tool-audit'>
                        {getAuditYaml(tool)
                          .split('\n')
                          .map((line, i) => (
                            <div key={i} className='yaml-line'>
                              {line}
                            </div>
                          ))}
                      </div>
                    )}

                    {/* Audit toggle link */}
                    <div className='tool-audit-link' onClick={toggleAudit}>
                      {showAudit ? (
                        <>
                          <DescriptionSquareIcon />
                          Show description
                        </>
                      ) : (
                        <>
                          <CodeSquareIcon />
                          More info
                        </>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ChatToolGroupDisplay;
